import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { authService } from '@services/authService';
import { companiesService } from '@services/companiesService';
import { companiesDGService, CompanyDGData } from '@services/companiesDGService';

interface CuiAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onCompanySelect?: (company: any) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  autoSearch?: boolean;
}

const CuiAutocomplete: React.FC<CuiAutocompleteProps> = ({
  value,
  onChange,
  onCompanySelect,
  label = 'CUI',
  placeholder = 'Introduceti CUI-ul companiei (2-10 cifre)',
  required = false,
  disabled = false,
  error = false,
  helperText = '',
  fullWidth = true,
  autoSearch = true,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null);
  const [searchError, setSearchError] = useState<string>('');
  const [showInfo, setShowInfo] = useState(false);
  const [dataSource, setDataSource] = useState<'anaf' | 'local' | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Cautare firme dupa CUI folosind serviciul ANAF
  const searchCompanies = async (inputValue: string) => {
    if (!inputValue || inputValue.length < 2) {
      setSearchError('Introduceti cel putin 2 caractere pentru CUI');
      return;
    }
    
    if (!/^\d{2,10}$/.test(inputValue)) {
      setSearchError('CUI-ul trebuie sa contina intre 2 si 10 cifre');
      return;
    }

    setLoading(true);
    setSearchError('');
    setOptions([]);
    setDataSource(null);

    try {
      // Cauta datele la ANAF (verificarea CUI-ului se face doar la butonul "Verifica CUI")
      const response = await companiesDGService.getCompanyDG(inputValue);
      console.log('🔍 Rezultate cautare CUI prin ANAF DG:', response);
      
      if (response.success && response.data) {
        const companyData = response.data;
        
        if (companyData.CUI && companyData.denumireCompanie) {
          // Firma gasita in ANAF
          setDataSource('anaf');
          
          console.log(`✅ Firma gasita in ANAF:`, companyData);
          
          if (onCompanySelect) {
            onCompanySelect(companyData);
          }
          
          setSearchError('');
        } else {
          setSearchError('Nu s-au gasit companii cu acest CUI');
        }
      } else {
        setSearchError('Nu s-au gasit companii cu acest CUI');
      }
    } catch (err: any) {
      console.error('❌ Eroare la cautarea companiilor:', err);
      setSearchError('Eroare la cautarea companiilor');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!autoSearch) {
      return;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchCompanies(inputValue);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [inputValue, autoSearch]);

  // Handler pentru selectarea unei firme
  const handleCompanySelect = (company: any | null) => {
    if (company) {
      setSelectedCompany(company);
      onChange(company.cui);
      setInputValue(company.denumire);
      setShowInfo(true);
      
      // Apeleaza onCompanySelect dupa ce onChange a fost procesat
      // pentru a permite parent-ului sa aiba CUI-ul setat
      setTimeout(() => {
        onCompanySelect?.(company);
      }, 0);
    }
  };

  // Handler pentru schimbarea input-ului
  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    
    // Daca se sterge tot, reseteaza selectia
    if (!newInputValue) {
      setSelectedCompany(null);
      setShowInfo(false);
    }
  };

  // Handler pentru schimbarea valorii
  const handleChange = (event: React.SyntheticEvent, newValue: any | null) => {
    handleCompanySelect(newValue);
  };

  // Refresh search
  const handleRefresh = () => {
    if (inputValue) {
      searchCompanies(inputValue);
    }
  };

  // Formatare optiuni pentru autocomplete
  const getOptionLabel = (option: any) => {
    return `${option.denumire} (${option.cui})`;
  };

  // Render optiuni personalizate
  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: any) => (
    <Box component="li" {...props}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
          {option.denumire}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
          <Chip 
            label={`CUI: ${option.cui}`} 
            size="small" 
            variant="outlined" 
            color="primary" 
          />
          <Chip 
            label={option.localitate} 
            size="small" 
            variant="outlined" 
            color="secondary" 
          />
          {option.coduriCAENRelevante && (
            <Chip 
              label={`CAEN: ${option.coduriCAENRelevante}`} 
              size="small" 
              variant="outlined" 
              color="success" 
            />
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <TextField
        fullWidth
        label={label}
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => {
          const value = e.target.value;
          setInputValue(value);
          setSearchError('');
          // Actualizeaza formData.cui prin onChange
          if (onChange) {
            onChange(value);
          }
          
          // Daca CUI-ul este valid (2-10 cifre), cauta automat in ONRC
          if (autoSearch && value && /^\d{2,10}$/.test(value)) {
            searchCompanies(value);
          }
        }}
        required={required}
        error={!!searchError}
        helperText={searchError}
        disabled={disabled}
        InputProps={{
          endAdornment: loading ? <CircularProgress color="inherit" size={20} /> : null,
        }}
      />

      {/* Informatii despre firma selectata */}
      {showInfo && selectedCompany && (
        <Alert 
          severity={dataSource === 'anaf' ? 'success' : 'info'} 
          icon={<BusinessIcon />}
          sx={{ mt: 2 }}
          action={
            <IconButton
              size="small"
              onClick={() => setShowInfo(false)}
            >
              <InfoIcon />
            </IconButton>
          }
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
              Firma gasita {dataSource === 'anaf' ? 'in registrul ANAF' : 'in baza de date locala'}:
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Denumire:</strong> {selectedCompany.denumire}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>CUI:</strong> {selectedCompany.cui}
            </Typography>
            {selectedCompany.forma_juridica && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Forma juridica:</strong> {selectedCompany.forma_juridica}
              </Typography>
            )}
            {selectedCompany.adresa && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Adresa:</strong> {selectedCompany.adresa}
              </Typography>
            )}
            {selectedCompany.judet && selectedCompany.oras && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Locatia:</strong> {selectedCompany.oras}, {selectedCompany.judet}
              </Typography>
            )}
            {selectedCompany.cod_caen && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Cod CAEN:</strong> {selectedCompany.cod_caen}
              </Typography>
            )}
            {selectedCompany.telefon && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Telefon:</strong> {selectedCompany.telefon}
              </Typography>
            )}
            {selectedCompany.email && (
              <Typography variant="body2">
                <strong>Email:</strong> {selectedCompany.email}
              </Typography>
            )}
            {dataSource === 'anaf' && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic', color: 'success.dark' }}>
                ✓ Date verificate prin API-ul oficial ANAF
              </Typography>
            )}
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default CuiAutocomplete; 

